import React, { useState, useRef } from "react"
import { useIntl } from "gatsby-plugin-intl"
import Loadable from "@loadable/component"

const AudioPlayer = Loadable(() => import("react-h5-audio-player"))

import ReplayIcon from "../assets/images/svg/replay.svg";

const AudioBlock = ({ title = null, assetUrl, standalone }) => {
  const intl = useIntl()

  const audioRef = useRef(null)

  const [played, setPlayed] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(false)
  const [duration, setDuration] = useState(false)

  const progressMinutes = Math.floor(progress / 60)
  const progressSeconds = Math.floor(progress % 60)

  const durationMinutes = Math.floor(duration / 60)
  const durationSeconds = Math.floor(duration % 60)

  const isBrowser = () => typeof window !== "undefined"
  
  function onPlay() {
    const event = new CustomEvent("audio-play", { detail: assetUrl })
    isBrowser() && window.dispatchEvent(event)

    setPlayed(true)
    setIsPlaying(true)
  }

  function onReplay() {
    const event = new CustomEvent("audio-play", { detail: assetUrl })
    isBrowser() && window.dispatchEvent(event)

    audioRef.current.audio.current.currentTime = 0
    audioRef.current.audio.current.play()

    setIsPlaying(true)
  }

  isBrowser() && window.addEventListener("audio-play", e => {
    if (e.detail === assetUrl) {
      return
    }

    audioRef.current.audio.current.pause()
    setIsPlaying(false)
  })

  function zeroPad(num, places) {
    var zero = places - num.toString().length + 1
    return Array(+(zero > 0 && zero)).join("0") + num
  }

  function handleProgressClick(e) {
    const rect = e.target.getBoundingClientRect()
    const x = e.clientX - rect.left

    const newProgress = ((x / rect.width) * duration).toFixed(2)

    audioRef.current.audio.current.currentTime = newProgress
    setProgress(newProgress)
  }

  return (
    <div className={`flex items-center gap-3 px-3 py-4 medium:p-4.75 pr-4.75 medium:pr-7 text-black tro-body-block-content__audio transition-colors ${standalone && "tro-body-block-content__audio--standalone col-span-full tablet:col-start-4 tablet:col-span-6 rounded-xl border mb-4"} ${isPlaying ? "bg-brand" : "bg-brand-lightgrey"}`}>
      <AudioPlayer
        onPlay={onPlay}
        onPause={() => setIsPlaying(false)}
        onLoadedMetaData={e => { setDuration(Number(e.target.duration)) }}
        onListen={e => setProgress(Number(e.target.currentTime))}
        onEnded={() => {
          setProgress(duration)
          setIsPlaying(false)
          setPlayed(false)

          setTimeout(() => {
            setProgress(0)
          }, 100)
        }}
        ref={audioRef}
        src={assetUrl}
        showJumpControls={false}
        hasDefaultKeyBindings={false}
        showDownloadProgress={false}
        listenInterval={100}
        customIcons={{
          play: <img src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='38' height='38' viewBox='0 0 38 38'><circle cx='19' cy='19' r='19' fill='%23636363'/><path d='m26.6,19l-11.4,6.58v-13.16l11.4,6.58Z' fill='white'/></svg>" />,
          pause: <img src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='38' height='38' viewBox='0 0 38 38'><circle cx='19' cy='19' r='19'/><rect x='12.95' y='12.28' width='4.03' height='13.43' fill='white'/><rect x='21.02' y='12.28' width='4.03' height='13.43' fill='white'/></svg>" />,
        }}
      />

      <div className="flex-grow font-sans">
        <div className={`flex items-center justify-between gap-4 w-full font-black leading-none text-special transform transition-transform ${played ? "-translate-y-1" : ""}`}>
          {title}

          {played && (
            <button
              className="opacity-30 hover:opacity-100 transition-opacity"
              onClick={onReplay}
            >
              <ReplayIcon className="w-2 h-2" />
            </button>
          )}
        </div>
        <div 
          className={`cursor-pointer relative h-1 overflow-hidden bg-white rounded-full transition-opacity ${played ? "" : "opacity-0"}`}
          onClick={handleProgressClick}
        >
          <div 
            className="absolute inset-0 h-1 bg-black rounded-full pointer-events-none transition-transform duration-100 ease-linear"
            style={{ transform: `translateX(-${100 - (progress / duration * 100)}%)` }} 
          />
        </div>
        <div className={`flex justify-between leading-none text-2xs transform transition-transform ${played ? "translate-y-1.5" : ""}`}>
          <span>
            {`${durationMinutes} ${intl.formatMessage({ id: durationMinutes === 1 ? "minute" : "minutes" })} ${zeroPad(durationSeconds, 2)} ${intl.formatMessage({ id: durationSeconds === 1 ? "second" : "seconds" })}`}
          </span>

          {played ? (
            <span>
              {`${zeroPad(progressMinutes, 2)}:${zeroPad(progressSeconds, 2)}`}
            </span>
          ) : null}
        </div>
        <div className="absolute left-0 visible w-full h-0.5 -bottom-1">
          <div className="absolute top-0 left-0 h-0.5" style={{ width: `50%` }}></div>
        </div>
      </div>
    </div>
  )
}

export default AudioBlock
